import {yup} from 'libs/yup'

export const sortTypes = [
  {value: 'staff_code__asc', label: 'スタッフコード 昇順'},
  {value: 'staff_code__desc', label: 'スタッフコード 降順'},
  {value: 'full_name_kana__asc', label: '氏名 昇順'},
  {value: 'full_name_kana__desc', label: '氏名 降順'},
  {value: 'office__asc', label: '適用事業所 設定順'},
  {value: 'office__desc', label: '適用事業所 設定逆順'},
  {value: 'employment_type__asc', label: '雇用形態 設定順'},
  {value: 'employment_type__desc', label: '雇用形態 設定逆順'},
  {value: 'group__asc', label: 'グループ 設定順'},
  {value: 'group__desc', label: 'グループ 設定逆順'},
  {value: 'position__asc', label: '役職 設定順'},
  {value: 'position__desc', label: '役職 設定逆順'},
  {value: 'occupation__asc', label: '職種 設定順'},
  {value: 'occupation__desc', label: '職種 設定逆順'},
  {value: 'joined_at__desc', label: '入社日 新しい順'},
  {value: 'joined_at__asc', label: '入社日 古い順'},
  {value: 'retired_at__desc', label: '退社日 新しい順'},
  {value: 'retired_at__asc', label: '退社日 古い順'},
]

export const employmentStatuses = [
  {value: 'all', label: 'すべて'},
  {value: 'employed', label: '在職中'},
  {value: 'vacation', label: '休職中'},
  {value: 'unemployed', label: '退職済'},
  {value: 'join', label: '内定'},
] as const

export type EmploymentStatus = (typeof employmentStatuses)[number]['value']

const joinedStatuses = [
  {value: '', label: '-'},
  {value: 'true', label: '加入'},
  {value: 'false', label: '未加入'},
  {value: 'null', label: '未入力'},
]

export const addOptions = [
  {label: '旧姓', value: 'former_name'},
  {label: '生年月日', value: 'birthday', fields: ['birthday_start', 'birthday_end']},
  {label: '性別 ', value: 'genders'},
  {label: '居住国', value: 'countries'},
  {label: '住所 都道府県', value: 'prefecture_ids'},
  {
    label: '国籍情報',
    value: 'national_types',
    fields: ['national_types', 'nation_ids', 'visa_type_ids'],
  },
  {label: '退職日', value: 'retired_at', fields: ['retired_at_start', 'retired_at_end']},
  {label: '退職区分', value: 'retire_types'},
  {label: '家族の有無', value: 'has_dependents'},
  {label: '配偶者の有無', value: 'marital_statuses'},
  {label: '給与形態', value: 'remuneration_types'},
  {label: '障害者区分', value: 'handicap_types'},
  {label: '寡婦（夫）区分 ', value: 'widow_types'},
  {label: '勤労学生区分', value: 'working_student_types'},
  {label: 'タグ', value: 'tags'},
  {label: 'スキル', value: 'skills'},
  {label: '資格', value: 'qualifications'},
  {
    label: '語学',
    value: 'languages',
    fields: [
      'languages',
      'language_levels',
      'languages_qualification_name',
      'languages_score_from',
      'languages_score_to',
    ],
  },
  {label: 'プロジェクト', value: 'projects'},
]

const mainFieldSchema = yup.object({
  q: yup.string(),
  status: yup
    .string()
    .oneOf(employmentStatuses.map((item) => item.value))
    .required(),
})

const additionalFieldSchema = yup.object({
  offices: yup.array(yup.string().required()).label('適用事業所'),
  groups: yup.array(yup.string().required()).label('グループ'),
  positions: yup.array(yup.string().required()).label('役職'),
  occupations: yup.array(yup.string().required()).label('職種'),
  employmentTypes: yup.array(yup.string().required()).label('雇用形態'),
  joinedHealthInsurance: yup
    .string()
    .oneOf(joinedStatuses.map((item) => item.value))
    .label('健康保険加入状況'),
  joinedWelfarePensionInsurance: yup
    .string()
    .oneOf(joinedStatuses.map((item) => item.value))
    .label('厚生年金保険加入状況'),
  joinedEmploymentInsurance: yup
    .string()
    .oneOf(joinedStatuses.map((item) => item.value))
    .label('雇用保険加入状況'),
  ageFrom: yup
    .number()
    .notRequired()
    .allowEmptyString()
    .range(0, 120)
    .typeError(({label}) => `${label}は半角数字で入力してください`)
    .test('age-correlation', '年齢の範囲が正しくありません', (ageFrom, context) => {
      const ageTo = context.parent.ageTo
      return !(ageFrom && ageTo && +ageFrom > +ageTo)
    })
    .label('年齢'),
  ageTo: yup
    .number()
    .notRequired()
    .allowEmptyString()
    .range(0, 120)
    .typeError(({label}) => `${label}は半角数字で入力してください`)
    .test('age-correlation', '年齢の範囲が正しくありません', (ageTo, context) => {
      const ageFrom = context.parent.ageFrom
      return !(ageFrom && ageTo && +ageFrom > +ageTo)
    })
    .label('年齢'),
  ageBaseDate: yup.string().dateFormat().label('年齢の基準日'),
  joinedAtStart: yup.string().dateFormat().label('入社日（開始）'),
  joinedAtEnd: yup.string().dateFormat().label('入社日（終了）'),
  formerName: yup.string().label('旧姓'),
  birthdayStart: yup.string().dateFormat().label('生年月日（開始）'),
  birthdayEnd: yup.string().dateFormat().label('生年月日（終了）'),
  genders: yup.array(yup.string().required()).label('性別'),
  countries: yup.array(yup.string().required()).label('居住国'),
  prefectureIds: yup.array(yup.string().required()).label('都道府県'),
  nationalTypes: yup.array(yup.string().required()).label('外国人区分'),
  nationIds: yup.array(yup.string().required()).label('国籍'),
  visaTypeIds: yup.array(yup.string().required()).label('在留資格'),
  retiredAtStart: yup.string().dateFormat().label('退職日（開始）'),
  retiredAtEnd: yup.string().dateFormat().label('退職日（終了）'),
  retireTypes: yup.array(yup.string().required()).label('退職区分'),
  hasDependents: yup.array(yup.string().required()).label('家族の有無'),
  maritalStatuses: yup.array(yup.string().required()).label('配偶者の有無'),
  remunerationTypes: yup.array(yup.string().required()).label('給与形態'),
  handicapTypes: yup.array(yup.string().required()).label('障害者区分'),
  widowTypes: yup.array(yup.string().required()).label('寡婦（夫）区分'),
  workingStudentTypes: yup.array(yup.string().required()).label('勤労学生区分'),
  tags: yup.array(yup.string().required()).label('タグ'),
  skills: yup.string().label('スキル'),
  qualifications: yup.string().label('資格'),
  languages: yup.string().notRequired().label('語学'),
  languageLevels: yup.string().notRequired().label('語学レベル'),
  languagesQualificationName: yup.string().label('語学資格'),
  languagesScoreFrom: yup
    .number()
    .notRequired()
    .allowEmptyString()
    .range(0, 100)
    .typeError(({label}) => `${label}は半角数字で入力してください`)
    .label('語学スコア（下限）'),
  languagesScoreTo: yup
    .number()
    .notRequired()
    .allowEmptyString()
    .range(0, 100)
    .typeError(({label}) => `${label}は半角数字で入力してください`)
    .test('score-correlation', '語学スコアの範囲が正しくありません', (scoreTo, context) => {
      const scoreFrom = context.parent.languagesScoreFrom
      return !(scoreFrom && scoreTo && +scoreFrom > +scoreTo)
    })
    .label('語学スコア（上限）'),
  projects: yup.string().label('プロジェクト'),
  addedFields: yup.array(
    yup
      .string()
      .oneOf(addOptions.map((item) => item.value))
      .required()
  ),
})

const sortTypeSchema = yup.object({
  sortType: yup
    .string()
    .oneOf(sortTypes.map((item) => item.value))
    .required(),
})

const paginatorSchema = yup.object({
  page: yup.string(),
  limit: yup.string(),
})

export const schema = mainFieldSchema.concat(additionalFieldSchema).concat(sortTypeSchema).concat(paginatorSchema)

export type Schema = yup.InferType<typeof schema>
